import React, { useState, useRef } from 'react';
import '@/App.css';
import Marquee from 'react-fast-marquee';
import { motion } from 'framer-motion';
import { 
  Phone, 
  MapPin, 
  Clock, 
  Mail, 
  Download,
  Wrench,
  Package,
  Users,
  TrendingUp,
  CheckCircle2,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const productCategories = [
  {
    id: 1,
    name: 'Cutting Discs & Abrasives',
    image: 'https://images.unsplash.com/photo-1513206837745-ad29c1493bc7',
    description: 'Cut-off wheels, grinding discs, TCT saw blades'
  },
  {
    id: 2,
    name: 'Measuring Tapes',
    image: 'https://images.pexels.com/photos/32942847/pexels-photo-32942847.jpeg',
    description: 'Steel and fibre measuring tapes, all sizes'
  },
  {
    id: 3,
    name: 'Sockets',
    image: 'https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/2d1c2aea5095b419a5068818cc5448646cffec4c2b54ffdec24aa99b651970db.png',
    description: 'Cold forged sockets, socket sets, hex sockets'
  },
  {
    id: 4,
    name: 'Spanners & Wrenches',
    image: 'https://images.pexels.com/photos/5853933/pexels-photo-5853933.jpeg',
    description: 'Combination spanners, adjustable wrenches, pipe wrenches'
  },
  {
    id: 5,
    name: 'Pliers',
    image: 'https://images.pexels.com/photos/6412542/pexels-photo-6412542.jpeg',
    description: 'Combination pliers, long nose, side cutting, grip locking'
  },
  {
    id: 6,
    name: 'Screwdrivers',
    image: 'https://images.unsplash.com/photo-1770386582823-3a7094e35b22',
    description: 'Engineer pattern, Phillips, reversible, screwdriver sets'
  },
  {
    id: 7,
    name: 'Cutters',
    image: 'https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/2d1c2aea5095b419a5068818cc5448646cffec4c2b54ffdec24aa99b651970db.png',
    description: 'Aviation snips, bolt cutters, cable cutters, hacksaw blades'
  },
  {
    id: 8,
    name: 'Carpentry Tools',
    image: 'https://images.unsplash.com/photo-1513206837745-ad29c1493bc7',
    description: 'Jack planes, wood chisels, pincers, centre punches'
  },
  {
    id: 9,
    name: 'Tool Boxes & Storage',
    image: 'https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/2d1c2aea5095b419a5068818cc5448646cffec4c2b54ffdec24aa99b651970db.png',
    description: 'Tool boxes, tool trolleys, storage kits'
  },
  {
    id: 10,
    name: 'Hammers',
    image: 'https://images.unsplash.com/photo-1513206837745-ad29c1493bc7',
    description: 'Ball pein, claw, cross pein, sledge hammers'
  },
  {
    id: 11,
    name: 'Allen Keys & Plumber Tools',
    image: 'https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/2d1c2aea5095b419a5068818cc5448646cffec4c2b54ffdec24aa99b651970db.png',
    description: 'Allen key sets, ratchet die sets, pipe die sets, bench vices'
  },
  {
    id: 12,
    name: 'Hydraulic Jacks & Lubrications',
    image: 'https://images.unsplash.com/photo-1513206837745-ad29c1493bc7',
    description: 'Hydraulic bottle jacks, grease guns, oil cans, barrel pumps'
  }
];

function App() {
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    phone: '',
    requirement: '',
    category_interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inquiryRef = useRef(null);

  const scrollToInquiry = () => {
    inquiryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCategoryClick = (categoryName) => {
    setFormData(prev => ({ ...prev, category_interest: categoryName }));
    scrollToInquiry();
  };

  const handleDownloadCatalog = async () => {
    try {
      window.open(`${API}/download-catalog`, '_blank');
      toast.success('Catalog download started!');
    } catch (error) {
      toast.error('Unable to download catalog. Please contact us.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiry`, formData);
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setFormData({
        name: '',
        business_name: '',
        phone: '',
        requirement: '',
        category_interest: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
      console.error('Inquiry submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="font-heading text-2xl font-bold uppercase tracking-tight text-secondary">
              Rishabh Marketing
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#about" className="text-sm font-body font-semibold hover:text-primary">ABOUT</a>
              <a href="#products" className="text-sm font-body font-semibold hover:text-primary">PRODUCTS</a>
              <a href="#contact" className="text-sm font-body font-semibold hover:text-primary">CONTACT</a>
              <Button 
                onClick={scrollToInquiry}
                className="bg-primary hover:bg-primary/90 text-white font-bold uppercase text-xs px-6"
                data-testid="header-cta-button"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-[85vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url('https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/4619fdab11a4337e7a5570c730d90a224023c72aabb7d22b57fb55fb2f9af119.png')`
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-primary px-6 py-2 mb-6">
              <span className="text-white font-heading font-bold text-sm uppercase tracking-widest">B2B ONLY • FLAT 40% OFF</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold uppercase tracking-tight text-white mb-6" data-testid="hero-heading">
              Wholesale Tools<br />At Half Price
            </h1>
            <p className="text-xl text-white/90 font-body max-w-2xl mx-auto mb-10 leading-relaxed">
              Muzaffarpur's trusted B2B supplier for quality hardware and tools. Get 40% discount on OTPL price book rates for bulk orders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToInquiry}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white font-heading font-bold uppercase text-base px-8 py-6"
                data-testid="hero-get-bulk-price-button"
              >
                Get Bulk Price
              </Button>
              <Button 
                onClick={handleDownloadCatalog}
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-secondary font-heading font-bold uppercase text-base px-8 py-6"
                data-testid="hero-download-catalog-button"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Price List
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee Banner */}
      <div className="bg-secondary py-4" data-testid="marquee-banner">
        <Marquee speed={50} gradient={false}>
          <span className="text-white font-heading font-bold text-xl uppercase tracking-widest mx-8">FLAT 40% DISCOUNT ON PRICE BOOK RATES</span>
          <span className="text-primary font-heading font-bold text-xl uppercase tracking-widest mx-8">•</span>
          <span className="text-white font-heading font-bold text-xl uppercase tracking-widest mx-8">B2B BUYERS ONLY</span>
          <span className="text-primary font-heading font-bold text-xl uppercase tracking-widest mx-8">•</span>
          <span className="text-white font-heading font-bold text-xl uppercase tracking-widest mx-8">OTPL/VENUS HAND TOOLS</span>
          <span className="text-primary font-heading font-bold text-xl uppercase tracking-widest mx-8">•</span>
          <span className="text-white font-heading font-bold text-xl uppercase tracking-widest mx-8">WHOLESALE SUPPLIES</span>
          <span className="text-primary font-heading font-bold text-xl uppercase tracking-widest mx-8">•</span>
        </Marquee>
      </div>

      {/* About Section */}
      <section id="about" className="py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl sm:text-5xl font-heading font-bold uppercase tracking-tight text-secondary mb-6">
                Your Trusted B2B Tool Partner
              </h2>
              <p className="text-base font-body leading-relaxed text-gray-700 mb-6">
                Rishabh Marketing Tool Store has been serving dealers, shopkeepers, and contractors in Muzaffarpur with quality hardware and tools at unbeatable wholesale prices.
              </p>
              <p className="text-base font-body leading-relaxed text-gray-700 mb-6">
                We offer <strong className="text-primary">FLAT 40% discount</strong> on official OTPL/Venus Hand Tools price book rates for all bulk orders. Our extensive catalog includes cutting discs, measuring tapes, spanners, pliers, screwdrivers, hydraulic jacks, and much more.
              </p>
              <div className="bg-accent border border-gray-200 p-6">
                <p className="text-sm font-heading font-bold uppercase tracking-widest text-secondary mb-2">
                  Latest Price List
                </p>
                <p className="text-base font-body text-gray-700">
                  Effective from <strong>March 2026</strong>
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/04aeb751-4c9f-479a-bb8a-deacdde84fc6/images/9dcc7862aa33013fab14ddd8c38f04e0aa5b92a6e2cc04efd2d521cb37a95746.png" 
                alt="Industrial Tools Display"
                className="w-full h-auto border border-gray-200"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section id="products" className="py-24 bg-muted" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold uppercase tracking-tight text-secondary mb-4">
              Product Categories
            </h2>
            <p className="text-base font-body text-gray-700 max-w-2xl mx-auto">
              Browse our extensive range of wholesale hardware and tools. Click on any category to request a quote.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {productCategories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="bg-white border border-gray-200 overflow-hidden group cursor-pointer"
                onClick={() => handleCategoryClick(category.name)}
                data-testid={`category-card-${category.id}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold uppercase tracking-tight text-secondary mb-2">
                    {category.name}
                  </h3>
                  <p className="text-sm font-body text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <Button 
                    onClick={() => handleCategoryClick(category.name)}
                    className="w-full bg-transparent border border-secondary text-secondary hover:bg-secondary hover:text-white font-heading font-bold uppercase text-xs"
                    data-testid={`request-quote-button-${category.id}`}
                  >
                    Request Quote
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold uppercase tracking-tight text-secondary mb-4">
              Why Choose Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-secondary text-white p-8 border border-gray-200" data-testid="feature-discount">
              <TrendingUp className="w-12 h-12 mb-4 text-primary" />
              <h3 className="text-2xl font-heading font-semibold uppercase mb-3">
                40% Discount
              </h3>
              <p className="text-sm font-body text-white/80">
                Get flat 40% off on official OTPL price book rates for all bulk orders.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-8" data-testid="feature-bulk">
              <Package className="w-12 h-12 mb-4 text-primary" />
              <h3 className="text-2xl font-heading font-semibold uppercase mb-3 text-secondary">
                Bulk Availability
              </h3>
              <p className="text-sm font-body text-gray-700">
                Large stock available for immediate delivery. Ideal for dealers and contractors.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-8" data-testid="feature-trust">
              <Users className="w-12 h-12 mb-4 text-primary" />
              <h3 className="text-2xl font-heading font-semibold uppercase mb-3 text-secondary">
                Trusted Partner
              </h3>
              <p className="text-sm font-body text-gray-700">
                Serving local businesses in Muzaffarpur with quality products and reliable service.
              </p>
            </div>
            <div className="bg-secondary text-white p-8 border border-gray-200" data-testid="feature-range">
              <Wrench className="w-12 h-12 mb-4 text-primary" />
              <h3 className="text-2xl font-heading font-semibold uppercase mb-3">
                Wide Range
              </h3>
              <p className="text-sm font-body text-white/80">
                Comprehensive selection of hand tools and hardware from OTPL/Venus.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* B2B Inquiry Form */}
      <section ref={inquiryRef} id="inquiry" className="py-24 bg-muted" data-testid="inquiry-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold uppercase tracking-tight text-secondary mb-6">
                Request a Quote
              </h2>
              <p className="text-base font-body leading-relaxed text-gray-700 mb-8">
                Fill out the form and we'll get back to you with the best wholesale prices for your requirements.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-semibold text-secondary uppercase text-sm">B2B Only</p>
                    <p className="text-sm font-body text-gray-600">Wholesale rates for dealers and bulk buyers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-semibold text-secondary uppercase text-sm">Best Prices</p>
                    <p className="text-sm font-body text-gray-600">40% discount on official price book rates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-semibold text-secondary uppercase text-sm">Quick Response</p>
                    <p className="text-sm font-body text-gray-600">We respond to all inquiries within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 p-8">
              <form onSubmit={handleSubmit} data-testid="inquiry-form">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="font-heading text-xs uppercase tracking-widest font-bold">Your Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-gray-300 focus:ring-primary focus:border-primary"
                      data-testid="inquiry-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_name" className="font-heading text-xs uppercase tracking-widest font-bold">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      required
                      className="border-gray-300 focus:ring-primary focus:border-primary"
                      data-testid="inquiry-business-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-heading text-xs uppercase tracking-widest font-bold">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="border-gray-300 focus:ring-primary focus:border-primary"
                      data-testid="inquiry-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category_interest" className="font-heading text-xs uppercase tracking-widest font-bold">Category Interest</Label>
                    <Input
                      id="category_interest"
                      value={formData.category_interest}
                      onChange={(e) => setFormData({ ...formData, category_interest: e.target.value })}
                      placeholder="e.g., Spanners & Wrenches"
                      className="border-gray-300 focus:ring-primary focus:border-primary"
                      data-testid="inquiry-category-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="requirement" className="font-heading text-xs uppercase tracking-widest font-bold">Your Requirement *</Label>
                    <Textarea
                      id="requirement"
                      value={formData.requirement}
                      onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
                      required
                      rows={4}
                      className="border-gray-300 focus:ring-primary focus:border-primary"
                      placeholder="Tell us about your bulk order requirements..."
                      data-testid="inquiry-requirement-textarea"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-heading font-bold uppercase"
                    data-testid="inquiry-submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Footer */}
      <footer id="contact" className="bg-secondary text-white py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-heading font-bold uppercase mb-4">Rishabh Marketing</h3>
              <p className="text-sm font-body text-white/80 mb-4">
                Your trusted B2B wholesale hardware and tools supplier in Muzaffarpur.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-primary" />
                <p className="text-sm font-body text-white/80">
                  Puran Chand Lane, Raghuwans Road<br />
                  Mahammadpur Kazi, Musahri<br />
                  Muzaffarpur, Bihar 842001
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-heading font-bold uppercase mb-4">Contact Info</h3>
              <div className="space-y-3">
                <a href="tel:09934888188" className="flex items-center gap-3 hover:text-primary" data-testid="footer-phone-link">
                  <Phone className="w-5 h-5 text-primary" />
                  <span className="font-body">099348 88188</span>
                </a>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="font-body">Open: 12 PM - 7 PM</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-heading font-bold uppercase mb-4">Location</h3>
              <a 
                href="https://www.google.com/maps?gs_lcrp=EgZjaHJvbWUqBwgBECEYoAEyBggAEEUYOTIHCAEQIRigATIHCAIQIRigATIHCAMQIRiPAjIHCAQQIRiPAjIHCAUQIRiPAtIBCjEyNTU1ajBqMTWoAgywAgHxBa-mza6a6dxh&um=1&ie=UTF-8&fb=1&gl=in&sa=X&geocode=KRcAKRr6EO05MRbkYOxVrWxz&daddr=Puran+Chand+Lane,+Raghuwans+Road,+Mahammadpur+Kazi,+Musahri,+Muzaffarpur,+Bihar+842001"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="footer-map-link"
              >
                <Button className="bg-primary hover:bg-primary/90 text-white font-heading font-bold uppercase text-xs">
                  <MapPin className="w-4 h-4 mr-2" />
                  View on Google Maps
                </Button>
              </a>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8">
            <p className="text-center text-sm font-body text-white/60">
              © 2026 Rishabh Marketing Tool Store. All rights reserved. B2B Wholesale Only.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <a
        href="https://wa.me/919934888188?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20bulk%20tool%20prices"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg"
        data-testid="whatsapp-floating-button"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <a
        href="tel:09934888188"
        className="fixed bottom-24 right-6 z-40 bg-primary hover:bg-primary/90 text-white p-4 rounded-full shadow-lg"
        data-testid="call-floating-button"
      >
        <Phone className="w-6 h-6" />
      </a>
    </div>
  );
}

export default App;
